import { useState } from "react";
import api, { errDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/CrudPage";
import { Input } from "../components/ui/input";
import {
  ShoppingCart, Boxes, Coins, Scale, FolderTree, Package, Users, UserCog,
  FileText, FileSpreadsheet, FileDown,
} from "lucide-react";
import { toast } from "sonner";

const REPORTS = [
  { type: "sales", title: "Satış Raporu", desc: "Tüm satış işlemleri, ödeme türleri ve karlar", icon: ShoppingCart },
  { type: "stock", title: "Stok Raporu", desc: "Ürün stok durumları ve depo bilgileri", icon: Boxes },
  { type: "profit", title: "Kar Raporu", desc: "Günlük ciro, brüt ve net kar özetleri", icon: Coins },
  { type: "income_expense", title: "Gelir Gider Raporu", desc: "Tüm gelir ve gider kayıtları", icon: Scale },
  { type: "categories", title: "Kategori Raporu", desc: "Kategori bazlı ürün dağılımı", icon: FolderTree },
  { type: "products", title: "Ürün Raporu", desc: "Ürün fiyatları ve kar marjları", icon: Package },
  { type: "customers", title: "Müşteri Raporu", desc: "Müşteri alışverişleri ve cari bakiyeler", icon: Users },
  { type: "staff", title: "Personel Raporu", desc: "Personel bazlı satış performansı", icon: UserCog },
];

const FORMATS = [
  { format: "pdf", label: "PDF", icon: FileText },
  { format: "excel", label: "Excel", icon: FileSpreadsheet },
  { format: "csv", label: "CSV", icon: FileDown },
];

const EXT = { pdf: "pdf", excel: "xlsx", csv: "csv" };

export default function Reports() {
  const { hasPerm } = useAuth();
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [downloading, setDownloading] = useState("");

  const download = async (rtype, format) => {
    setDownloading(`${rtype}-${format}`);
    try {
      const res = await api.get(`/reports/${rtype}/export`, {
        params: { format, date_from: dateFrom, date_to: dateTo },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${rtype}_raporu.${EXT[format]}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Rapor indirildi");
    } catch (e) { toast.error(errDetail(e)); }
    finally { setDownloading(""); }
  };

  return (
    <div data-testid="reports-page">
      <PageHeader title="Raporlar" subtitle="PDF, Excel ve CSV formatında rapor alın">
        <div className="flex items-center gap-2">
          <Input data-testid="report-date-from" type="date" className="w-40 rounded-none" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          <span className="text-gray-400">-</span>
          <Input data-testid="report-date-to" type="date" className="w-40 rounded-none" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </PageHeader>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-3">
        {REPORTS.map((r) => (
          <div key={r.type} className="border border-gray-200 bg-white p-6" data-testid={`report-card-${r.type}`}>
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center bg-[#0B1F3A]/5 text-[#0B1F3A]">
                <r.icon size={22} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-heading font-semibold text-[#0B1F3A]">{r.title}</h3>
                <p className="mt-1 text-sm text-gray-500">{r.desc}</p>
              </div>
            </div>
            {hasPerm("reports", "export") && (
              <div className="mt-5 flex gap-2">
                {FORMATS.map((f) => (
                  <button key={f.format} data-testid={`export-${r.type}-${f.format}`}
                    disabled={downloading === `${r.type}-${f.format}`}
                    onClick={() => download(r.type, f.format)}
                    className="flex flex-1 items-center justify-center gap-1.5 border border-gray-200 py-2 text-xs font-semibold uppercase tracking-wide text-[#0B1F3A] transition-colors duration-200 hover:border-[#D4AF37] disabled:opacity-50">
                    <f.icon size={14} /> {downloading === `${r.type}-${f.format}` ? "..." : f.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
